import { createContext, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services";
import { useTranslation } from "react-i18next";
import jwt from "jwt-decode";
import { IProviderProps } from "../tenant/tenant.interfaces";
import {
  ICreateUser,
  ILogin,
  IResetPassword,
  ISendPasswordRecoveryCode,
  IUpdatePassword,
  IUpdateUser,
  IUser,
  IUserFromTenant,
} from "./user.interface";
import { IRole } from "../roles/roles.interface";

export const UserContext = createContext<any | null>(null);
export const UserProvider = ({ children }: IProviderProps) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [isLogged, setIsLogged] = useState(false);
  const [token, setToken] = useState(
    window.localStorage.getItem("EPA-Session-Token") || ""
  );
  const [accessToken, setAccessToken] = useState(
    window.localStorage.getItem("EPA-Access-Token") || ""
  );
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname.split("/")[1];
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(true);
  const roleAdmin = ["SystemAdmin"];
  const [apiLoading, setApiLoading] = useState(false);
  const [pageMounting, setPageMounting] = useState(false);
  const verifyUserLoggedIn = async () => {
    setApiLoading(true);
    const forceChangePassword = window.localStorage.getItem(
      "EPA-FORCE-CHANGE-PASSWORD"
    );
    if (forceChangePassword) {
      return;
    }
    if (token) {
      const validToken = await verifyToken();
      if (!validToken) {
        const customId = "custom-id-toast";
        toast.error(t("session_expired"), {
          toastId: customId,
        });

        setApiLoading(false);
        return Logout();
      }

      setIsLogged(true);
      setApiLoading(false);
      return true;
    } else {
      setApiLoading(false);
    }
  };

  const Login = async (data: ILogin) => {
    data.path = path;
    setApiLoading(true);
    await api
      .post("/login", data)
      .then(async (res) => {
        if (res.data.forceChangePassword) {
          window.localStorage.setItem("EPA-FORCE-CHANGE-PASSWORD", "TRUE");
          window.localStorage.setItem("EPA-USER-EMAIL", data.email);
          window.localStorage.setItem(
            "EPA-PASSWORD-SESSION",
            res.data.sessionToken
          );
          navigate(`/${path}/changepassword`);
          setApiLoading(false);
          return true;
        }
        window.localStorage.removeItem("EPA-FORCE-CHANGE-PASSWORD");
        window.localStorage.removeItem("EPA-USER-EMAIL");
        window.localStorage.removeItem("EPA-PASSWORD-SESSION");
        window.localStorage.setItem("EPA-Session-Token", res.data.IdToken);
        window.localStorage.setItem("EPA-Access-Token", res.data.AccessToken);
        window.localStorage.setItem("EPA-Refresh-Token", res.data.RefreshToken);
        setToken(res.data.IdToken);
        setAccessToken(res.data.AccessToken);
        setApiLoading(false);
        const tokenDecoded = jwt<any>(res.data.IdToken);
        setIsActive(true);
        setIsLogged(true);
        await verifyToken();
      })
      .catch((err) => {
        setApiLoading(false);

        if (err.response.data.message === "User is not confirmed.") {
          toast.error(t("user_unconfirmed"));
        } else {
          toast.error(t("invalid_login"));
        }
      });
  };

  const createUser = async (data: ICreateUser) => {
    setApiLoading(true);
    const register = await api
      .post("/users", data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        setApiLoading(false);
        toast.success(t("user_created"));
        return true;
      })
      .catch((err) => {
        setApiLoading(false);
        if (err.response.data.message === "Email already exists") {
          toast.error(t("email_duplicated"));
          return false;
        } else {
          toast.error(err.response.data.message);
          return false;
        }
      });

    if (register) {
      return true;
    }
  };

  const Logout = () => {
    window.localStorage.clear();
    window.location.reload();
  };
  const getUser = () => {
    setApiLoading(true);

    api
      .get("/user", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        setApiLoading(false);
        setUser(res.data);
      })
      .catch((err) => {
        console.log(err.response.data.message);
        setApiLoading(false);
      });
  };

  const updateUser = async (data: IUpdateUser) => {
    setApiLoading(true);
    if (data.confirm) {
      delete data.confirm;
    }
    await api
      .patch(`/users`, data, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(async (res) => {
        setApiLoading(false);
        getUser();
        navigate(`/${path}/profile`);
      })
      .catch((err) => {
        setApiLoading(false);

        if (err.response.data.message === "Email already exists") {
          toast.error(t("email_duplicated"));
        }
        toast.error(err.response.data.message);
        console.log(err.response);
      });
  };

  const verifyToken = async () => {
    setApiLoading(true);
    const tokenVerify = window.localStorage.getItem("EPA-Session-Token") || "";
    // if (tokenVerify) {
    //   const decodedToken: any = jwt(tokenVerify);
    //   const currentTime = Date.now() / 1000;

    //   // 12 hours
    //   if (decodedToken.exp - currentTime > 6 * 60 * 60) {
    //     const renewed = await renewToken();
    //   }
    // }
    const renewed = await renewToken();
    let verify;
    await api
      .get("/user", {
        headers: { Authorization: `Bearer ${tokenVerify}` },
      })
      .then((res) => {
        verify = true;
        setIsActive(true);
        setUser(res.data);
      })
      .catch((err) => {
        if (err.response.data.message === "User inactive") {
          verify = [true];
          setIsActive(true);
        } else {
          verify = false;
        }
      });
    if (!verify) {
      setApiLoading(false);

      return false;
    }
    setApiLoading(false);

    return verify;
  };

  const renewToken = async () => {
    setApiLoading(true);
    try {
      const response = await api.post("/token/refresh", {
        refreshToken: window.localStorage.getItem("EPA-Refresh-Token"),
      });
      window.localStorage.setItem("EPA-Session-Token", response.data.IdToken);
      window.localStorage.setItem(
        "EPA-Access-Token",
        response.data.AccessToken
      );
      setToken(response.data.IdToken);
      setAccessToken(response.data.AccessToken);

      setApiLoading(false);
      return true;
    } catch (error) {
      console.error("Error renewing token:", error);
      setApiLoading(false);
      return false;
    }
  };

  const sendActivationToken = async (path: string) => {
    setApiLoading(true);
    api
      .post(
        `/users/activate/email`,
        { path },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => {
        setApiLoading(false);
        toast.success(t("sent_code"));
      })
      .catch((err) => {
        setApiLoading(false);
        toast.error(t("something_wrong"));
      });
  };
  const activateUser = async (
    code: string,
    email: string,
    tenantId: string
  ) => {
    setApiLoading(true);
    api
      .post(
        `/users/activate`,
        { code, email, tenantId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then(async (res) => {
        toast.success(t("user_confirmed"));
        setApiLoading(false);
        navigate("/" + path);
      })
      .catch((err) => {
        setApiLoading(false);
        console.log(err.response.data);
        toast.error("Link invalido ou expirado.");
        navigate("/" + path);
      });
    setApiLoading(false);
  };
  const updatePassword = async (data: IUpdatePassword) => {
    delete data.confirm;
    setApiLoading(true);
    api
      .post(`/users/password`, data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        setApiLoading(false);
        toast.success(t("changed_password"));
      })
      .catch((err) => {
        setApiLoading(false);
        toast.error(err.response.data.message);
      });
  };
  const forceChangePassword = async (data: IUpdatePassword) => {
    delete data.confirm;
    setApiLoading(true);
    api
      .post(`/users/forcechangepassword`, data, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        setApiLoading(false);
        window.localStorage.removeItem("EPA-FORCE-CHANGE-PASSWORD");
        window.localStorage.removeItem("EPA-USER-EMAIL");
        window.localStorage.removeItem("EPA-PASSWORD-SESSION");
        toast.success(t("changed_password"));
        navigate(`/${path}`);
      })
      .catch((err) => {
        setApiLoading(false);
        toast.error(err.response.data.message);
      });
  };
  const resetPassword = async (data: IResetPassword) => {
    delete data.confirm;
    data.path = path;
    setApiLoading(true);
    api
      .post(`/users/password/reset`, data)
      .then((res) => {
        setApiLoading(false);
        toast.success(t("changed_password"));
        navigate("/" + path);
      })
      .catch((err) => {
        setApiLoading(false);
        console.log(err.response.data.message);
        if (err.response.data.message === "Incorrect username or password.") {
          toast.error(t("wrong_password"));
        } else {
          toast.error(err.response.data.message);
        }
      });
  };
  const sendPasswordToken = async (
    data: ISendPasswordRecoveryCode,
    setPage: React.Dispatch<React.SetStateAction<number>>
  ) => {
    data.path = path;
    setApiLoading(true);
    await api
      .post(`/users/password/recovery`, data)
      .then((res) => {
        setApiLoading(false);
        toast.success(t("recovery_code_sent"));
        if (setPage) setPage(1);
      })
      .catch((err) => {
        setApiLoading(false);
        console.log(err.response.data.message);
        toast.error(err.response.data.message);
      });
  };

  const getRoles = async () => {
    setApiLoading(true);
    const data = await api
      .get(`/roles/user`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setApiLoading(false);
        return res.data;
      })
      .catch((err) => {
        setApiLoading(false);
        console.log(err.response.data.message);
        return false;
      });
    return data;
  };

  const verifyRolePermission = (rolesArray: string[]) => {
    const canAccess = user?.roles
      .map((role: IRole) => role.name)
      .some((role: string) => rolesArray.includes(role));
    if (canAccess) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        getRoles,
        setUser,
        verifyUserLoggedIn,
        token,
        forceChangePassword,
        verifyRolePermission,
        activateUser,
        isLogged,
        Logout,
        getUser,
        createUser,
        resetPassword,
        Login,
        updateUser,
        apiLoading,
        roleAdmin,
        isActive,
        sendPasswordToken,

        sendActivationToken,
        pageMounting,
        setApiLoading,
        setPageMounting,
        updatePassword,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
